import { ControlPosition, FullscreenControl } from "maplibre-gl";
import React from "react";
import { useControl } from "react-map-gl/maplibre";

class FullscreenControlWithInPageFallback extends FullscreenControl {
    _requestFullscreen(): void {
        if (!this._container.requestFullscreen) {
            super._requestFullscreen();
            return;
        }
        this._container
            .requestFullscreen()
            .catch(() => this._expandWithinPageIfStillOnMap());
    }

    _expandWithinPageIfStillOnMap(): void {
        if (this._map) {
            this._togglePseudoFullScreen();
            document.addEventListener("keydown", this._restoreOnEscape);
        }
    }

    _restoreOnEscape = (event: KeyboardEvent): void => {
        if (event.key === "Escape" && this._isExpandedWithinPage()) {
            this._restoreWithinPage();
        }
    };

    _isExpandedWithinPage(): boolean {
        return this._container.classList.contains("maplibregl-pseudo-fullscreen");
    }

    _restoreWithinPage(): void {
        document.removeEventListener("keydown", this._restoreOnEscape);
        this._togglePseudoFullScreen();
    }

    _exitFullscreen(): void {
        if (this._isExpandedWithinPage()) {
            this._restoreWithinPage();
            return;
        }
        super._exitFullscreen();
    }

    onRemove(): void {
        document.removeEventListener("keydown", this._restoreOnEscape);
        super.onRemove();
    }
}

export const createFullscreenControlWithFallback = (
    onToggle: () => void,
): FullscreenControl => {
    const control = new FullscreenControlWithInPageFallback({});
    control.on("fullscreenstart", onToggle);
    control.on("fullscreenend", onToggle);
    return control;
};

interface FullscreenControlWithFallbackProps {
    position: ControlPosition;
    onToggle: () => void;
}

export const FullscreenControlWithFallback: React.FC<
    FullscreenControlWithFallbackProps
> = props => {
    const onToggleRef = React.useRef(props.onToggle);
    React.useEffect(() => {
        onToggleRef.current = props.onToggle;
    }, [props.onToggle]);
    useControl(() => createFullscreenControlWithFallback(() => onToggleRef.current()), {
        position: props.position,
    });
    return null;
};
